import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Headset, CircleNotch, Sparkle, Copy, CheckCircle, UserCircle, Robot,
  ClipboardText, ListChecks, ChatCircleDots, Phone, SmileySad, SmileyXEyes, Smiley, ArrowClockwise, PaperPlaneRight,
} from "@phosphor-icons/react";

function SentimentChip({ s }) {
  if (!s) return null;
  const map = {
    happy: { icon: Smiley, color: "#16A34A", label: "Nöjd" },
    neutral: { icon: ChatCircleDots, color: "#0EA5E9", label: "Neutral" },
    frustrated: { icon: SmileySad, color: "#F59E0B", label: "Frustrerad" },
    angry: { icon: SmileyXEyes, color: "#EF4444", label: "Arg" },
  };
  const m = map[s] || map.neutral;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ color: m.color, background: `${m.color}1A` }}>
      <m.icon size={12} weight="fill" /> {m.label}
    </span>
  );
}

export default function CoPilot() {
  const [queue, setQueue] = useState(null);
  const [selected, setSelected] = useState(null); // conversation detail
  const [assist, setAssist] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [assisting, setAssisting] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get("/copilot/queue");
      setQueue(data);
    } catch { setQueue({ queue: [], count: 0 }); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const openConversation = async (item) => {
    setSelected(null); setAssist(null); setReplyText(""); setLoadingDetail(true);
    try {
      const { data } = await api.get(`/conversations/${item.id}`);
      setSelected({ ...data, _meta: item });
    } catch { toast.error("Kunde inte öppna konversationen"); }
    finally { setLoadingDetail(false); }
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || !selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/copilot/conversations/${selected.id}/reply`, { text });
      setSelected((s) => ({ ...s, messages: [...(s.messages || []), data.message] }));
      setReplyText("");
      toast.success("Svar skickat till konversationen");
    } catch { toast.error("Kunde inte skicka svaret"); }
    finally { setSending(false); }
  };

  const generateAssist = async () => {
    if (!selected) return;
    setAssisting(true);
    try {
      const { data } = await api.post(`/copilot/conversations/${selected.id}/assist`);
      setAssist(data);
    } catch { toast.error("Kunde inte generera förslag"); }
    finally { setAssisting(false); }
  };

  const resolve = async () => {
    if (!selected) return;
    try {
      await api.post(`/copilot/conversations/${selected.id}/resolve`);
      toast.success("Ärendet markerat som löst");
      setSelected(null); setAssist(null);
      loadQueue();
    } catch { toast.error("Kunde inte markera som löst"); }
  };

  const copy = (text, idx) => {
    navigator.clipboard?.writeText(text);
    setCopiedIdx(idx);
    toast.success("Kopierat till urklipp");
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="copilot-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight flex items-center gap-2.5">
            <Headset size={28} weight="bold" className="text-[#0033FF]" /> AI Co-pilot
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Eskalerade ärenden med AI-sammanfattning och färdiga svarsförslag för dina handläggare.</p>
        </div>
        <button data-testid="copilot-refresh" onClick={loadQueue}
          className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm">
          <ArrowClockwise size={15} weight="bold" /> Uppdatera
        </button>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {/* queue */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden self-start">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Köande ärenden</span>
            <span className="text-xs font-bold bg-[#0033FF]/10 text-[#0033FF] rounded-full px-2 py-0.5" data-testid="copilot-queue-count">{queue?.count ?? 0}</span>
          </div>
          {queue === null ? (
            <div className="p-8 flex justify-center"><CircleNotch size={22} className="animate-spin text-zinc-300" /></div>
          ) : queue.count === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-400" data-testid="copilot-empty">
              <CheckCircle size={26} weight="duotone" className="mx-auto mb-2 text-green-400" />
              Inga eskalerade ärenden just nu — agenterna sköter allt själva! 🎉
            </div>
          ) : (
            <div className="divide-y divide-zinc-50 max-h-[70vh] overflow-y-auto scroll-thin">
              {queue.queue.map((item) => (
                <button key={item.id} data-testid="copilot-queue-item" onClick={() => openConversation(item)}
                  className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors ${selected?.id === item.id ? "bg-[#0033FF]/5" : ""}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold truncate">{item.agent_name || "Agent"}</span>
                    <span className="shrink-0">{item.channel === "voice" ? <Phone size={13} className="text-zinc-400" /> : <ChatCircleDots size={13} className="text-zinc-400" />}</span>
                  </div>
                  <p className="text-xs text-zinc-500 line-clamp-2">{item.last_user_message || "—"}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <SentimentChip s={item.sentiment} />
                    <span className="text-[10px] text-zinc-400">{(item.updated_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* detail */}
        <div className="min-w-0">
          {!selected && !loadingDetail && (
            <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center text-zinc-400" data-testid="copilot-detail-empty">
              <Headset size={32} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
              Välj ett ärende till vänster för att se konversationen och få AI-stöd.
            </div>
          )}
          {loadingDetail && <div className="bg-white border border-zinc-200 rounded-xl p-12 flex justify-center"><CircleNotch size={24} className="animate-spin text-zinc-300" /></div>}

          {selected && (
            <div className="space-y-5" data-testid="copilot-detail">
              {/* AI assist panel */}
              <div className="rounded-xl border border-[#0033FF]/20 bg-gradient-to-br from-[#0033FF]/[0.04] to-transparent p-5">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <span className="font-display font-bold text-lg flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-[#0033FF]" /> AI-medhjälpare</span>
                  <div className="flex items-center gap-2">
                    <button data-testid="copilot-assist-button" onClick={generateAssist} disabled={assisting}
                      className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-60">
                      {assisting ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
                      {assist ? "Uppdatera förslag" : "Generera förslag"}
                    </button>
                    <button data-testid="copilot-resolve-button" onClick={resolve}
                      className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm text-green-600">
                      <CheckCircle size={15} weight="bold" /> Markera löst
                    </button>
                  </div>
                </div>

                {!assist && !assisting && <p className="text-sm text-zinc-400">Klicka "Generera förslag" så sammanfattar AI:n ärendet och tar fram färdiga svar.</p>}
                {assist && (
                  <div className="space-y-4">
                    <div data-testid="copilot-summary">
                      <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1"><ClipboardText size={13} weight="bold" /> Sammanfattning</div>
                      <p className="text-sm text-zinc-700">{assist.summary}</p>
                      {assist.customer_intent && <p className="text-xs text-zinc-500 mt-1"><b>Kundens mål:</b> {assist.customer_intent}</p>}
                    </div>
                    {assist.suggestions?.length > 0 && (
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-2"><ChatCircleDots size={13} weight="bold" /> Svarsförslag</div>
                        <div className="space-y-2">
                          {assist.suggestions.map((s, i) => (
                            <div key={i} data-testid="copilot-suggestion" className="group flex items-start gap-2 bg-white border border-zinc-200 rounded-lg p-3 text-sm">
                              <span className="flex-1 text-zinc-700">{s}</span>
                              {selected?._meta?.send_enabled && (
                                <button onClick={() => setReplyText(s)} className="shrink-0 text-[11px] font-semibold text-[#0033FF] hover:underline" title="Använd som svar">Använd</button>
                              )}
                              <button onClick={() => copy(s, i)} className="shrink-0 text-zinc-400 hover:text-[#0033FF]" title="Kopiera">
                                {copiedIdx === i ? <CheckCircle size={16} weight="fill" className="text-green-500" /> : <Copy size={16} weight="bold" />}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {assist.next_steps?.length > 0 && (
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-2"><ListChecks size={13} weight="bold" /> Nästa steg</div>
                        <ul className="space-y-1">
                          {assist.next_steps.map((s, i) => (
                            <li key={i} className="text-sm text-zinc-600 flex items-start gap-2"><CheckCircle size={15} weight="bold" className="text-[#0033FF] mt-0.5 shrink-0" />{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* compose & send (opt-in per agent) */}
              {selected?._meta?.send_enabled && (
                <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="copilot-compose">
                  <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Svara kunden</div>
                  <textarea data-testid="copilot-reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                    rows={3} placeholder="Skriv ett svar eller klicka 'Använd' på ett förslag ovan…"
                    className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none resize-none" />
                  <div className="flex justify-end mt-2">
                    <button data-testid="copilot-send-reply" onClick={sendReply} disabled={sending || !replyText.trim()}
                      className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-50">
                      {sending ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneRight size={15} weight="fill" />} Skicka svar
                    </button>
                  </div>
                </div>
              )}

              {/* transcript */}
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Konversation</div>
                <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto scroll-thin">
                  {(selected.messages || []).map((m, i) => (
                    <div key={i} className={`flex gap-2 text-sm ${m.role === "user" ? "" : ""}`}>
                      <span className="shrink-0 mt-0.5">
                        {m.role === "user" ? <UserCircle size={18} weight="fill" className="text-zinc-400" /> : <Robot size={18} weight="fill" className="text-[#0033FF]" />}
                      </span>
                      <div className={`rounded-lg px-3 py-2 ${m.role === "user" ? "bg-zinc-100 text-zinc-800" : "bg-[#0033FF]/5 text-zinc-800"}`}>
                        {m.by === "human" && <span className="block text-[10px] font-bold uppercase tracking-wider text-[#0033FF] mb-0.5">Handläggare</span>}
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
